// src/components/admin/attendance/AttendanceAnalytics.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, subMonths, getDaysInMonth, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { useTheme } from '../../../contexts/ThemeContext';
import {
    BarChart3, TrendingUp, Calendar, Grid3X3,
    Download, Loader2, RefreshCw, X, Search,
    CheckCircle, XCircle, AlertCircle, Clock, ChevronDown, ChevronUp
} from 'lucide-react';
import {
    StatCard, MetricGrid, ChartContainer,
    LineChartComponent, BarChartComponent, PieChartComponent, AreaChartComponent,
    EmptyState, LoadingState, CHART_COLORS
} from '../../common/charts/ChartComponents';
import AnalyticsFilters from '../../common/filters/AnalyticsFilters';
import analyticsService from '../../../services/analytics.service';

// ─── Sub-Tab Definitions ───────────────────────────────────────────────
const SUB_TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'trends', label: 'Trends', icon: TrendingUp },
    { id: 'leaves', label: 'Leaves', icon: Calendar },
    { id: 'heatmap', label: 'Heatmap', icon: Grid3X3 },
];

// ─── Heatmap Status Colors ─────────────────────────────────────────────
const STATUS_COLORS = {
    present: { light: 'bg-green-500', dark: 'bg-green-600', label: 'Present' },
    absent: { light: 'bg-red-500', dark: 'bg-red-600', label: 'Absent' },
    half_day: { light: 'bg-yellow-500', dark: 'bg-yellow-600', label: 'Half Day' },
    halfDay: { light: 'bg-yellow-500', dark: 'bg-yellow-600', label: 'Half Day' },
    leave: { light: 'bg-blue-500', dark: 'bg-blue-600', label: 'On Leave' },
    on_leave: { light: 'bg-blue-500', dark: 'bg-blue-600', label: 'On Leave' },
    weekend: { light: 'bg-gray-300', dark: 'bg-gray-600', label: 'Weekend' },
    holiday: { light: 'bg-purple-400', dark: 'bg-purple-600', label: 'Holiday' },
    wfh: { light: 'bg-teal-500', dark: 'bg-teal-600', label: 'WFH' },
    work_from_home: { light: 'bg-teal-500', dark: 'bg-teal-600', label: 'WFH' },
};

const DEFAULT_STATUS_COLOR = { light: 'bg-gray-100', dark: 'bg-gray-800', label: 'No Data' };

// ─── Helper: compute previous period dates for comparison ──────────────
const getPreviousPeriod = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = end.getTime() - start.getTime();
    const prevEnd = new Date(start.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - diff);
    return {
        previousStart: format(prevStart, 'yyyy-MM-dd'),
        previousEnd: format(prevEnd, 'yyyy-MM-dd'),
    };
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const AttendanceAnalytics = () => {
    const { isDarkMode } = useTheme();

    // ── Navigation ──
    const [activeSubTab, setActiveSubTab] = useState('overview');

    // ── Filters ──
    const [filters, setFilters] = useState({
        period: 'month',
        startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
        selectedDepartments: [],
        selectedDesignations: [],
        selectedLocations: [],
        selectedEmploymentTypes: [],
        compareWithPrevious: false,
    });

    // ── Data ──
    const [attendanceData, setAttendanceData] = useState(null);
    const [deptAttendance, setDeptAttendance] = useState([]);
    const [leaveData, setLeaveData] = useState(null);
    const [leaveBalance, setLeaveBalance] = useState([]);
    const [comparisonData, setComparisonData] = useState(null);
    const [heatmapData, setHeatmapData] = useState(null);
    const [dailyDetail, setDailyDetail] = useState(null);

    // ── UI ──
    const [loading, setLoading] = useState({ main: true, heatmap: false, drilldown: false, export: false });
    const [error, setError] = useState({ main: null, heatmap: null });
    const [drilldown, setDrilldown] = useState({ open: false, type: null, label: '', data: [] });
    const [heatmapMonth, setHeatmapMonth] = useState(format(new Date(), 'yyyy-MM'));
    const [balanceSearch, setBalanceSearch] = useState('');
    const [balanceShowAll, setBalanceShowAll] = useState(false);

    // ── Month options for heatmap ──
    const monthOptions = useMemo(() => {
        const opts = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const d = subMonths(now, i);
            opts.push({ value: format(d, 'yyyy-MM'), label: format(d, 'MMM yyyy') });
        }
        return opts;
    }, []);

    // ─── Data Fetching ─────────────────────────────────────────────────
    const loadMainData = useCallback(async () => {
        setLoading(prev => ({ ...prev, main: true }));
        setError(prev => ({ ...prev, main: null }));
        try {
            const { startDate, endDate } = filters;

            const promises = [
                analyticsService.getAttendanceAnalytics(startDate, endDate),
                analyticsService.getDepartmentAttendance(startDate, endDate),
                analyticsService.getLeaveAnalytics(startDate, endDate),
                analyticsService.getLeaveBalance(),
            ];

            if (filters.compareWithPrevious) {
                const { previousStart, previousEnd } = getPreviousPeriod(startDate, endDate);
                promises.push(analyticsService.getComparisonAnalytics(startDate, endDate, previousStart, previousEnd));
            }

            const results = await Promise.allSettled(promises);

            if (results[0].status === 'fulfilled') setAttendanceData(results[0].value?.data || results[0].value);
            if (results[1].status === 'fulfilled') setDeptAttendance(results[1].value?.data || results[1].value || []);
            if (results[2].status === 'fulfilled') setLeaveData(results[2].value?.data || results[2].value);
            if (results[3].status === 'fulfilled') setLeaveBalance(results[3].value?.data || results[3].value || []);
            if (filters.compareWithPrevious && results[4]?.status === 'fulfilled') {
                setComparisonData(results[4].value?.data || results[4].value);
            } else {
                setComparisonData(null);
            }
        } catch (err) {
            console.error('Error loading attendance analytics:', err);
            setError(prev => ({ ...prev, main: err.message }));
        } finally {
            setLoading(prev => ({ ...prev, main: false }));
        }
    }, [filters]);

    const loadHeatmapData = useCallback(async () => {
        setLoading(prev => ({ ...prev, heatmap: true }));
        setError(prev => ({ ...prev, heatmap: null }));
        try {
            const result = await analyticsService.getAttendanceHeatmap(heatmapMonth);
            setHeatmapData(result?.data || result);
        } catch (err) {
            console.error('Error loading heatmap:', err);
            setError(prev => ({ ...prev, heatmap: err.message }));
        } finally {
            setLoading(prev => ({ ...prev, heatmap: false }));
        }
    }, [heatmapMonth]);

    const loadDrilldownData = useCallback(async (type, label, date) => {
        setLoading(prev => ({ ...prev, drilldown: true }));
        try {
            const targetDate = date || filters.endDate || format(new Date(), 'yyyy-MM-dd');
            const result = await analyticsService.getDailyAttendance(targetDate);
            let employees = result?.data?.employees || result?.employees || [];

            if (type === 'department') {
                employees = employees.filter(e =>
                    (e.department || '').toLowerCase() === label.toLowerCase()
                );
            }

            setDrilldown({ open: true, type, label: label || targetDate, data: employees });
        } catch (err) {
            console.error('Error loading drill-down:', err);
            setDrilldown({ open: true, type, label, data: [] });
        } finally {
            setLoading(prev => ({ ...prev, drilldown: false }));
        }
    }, [filters.endDate]);

    const handleExport = useCallback(async () => {
        setLoading(prev => ({ ...prev, export: true }));
        try {
            await analyticsService.exportAnalytics('attendance', {
                startDate: filters.startDate,
                endDate: filters.endDate,
            });
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setLoading(prev => ({ ...prev, export: false }));
        }
    }, [filters.startDate, filters.endDate]);

    // ── Effects ──
    useEffect(() => { loadMainData(); }, [loadMainData]);
    useEffect(() => {
        if (activeSubTab === 'heatmap') loadHeatmapData();
    }, [activeSubTab, loadHeatmapData]);

    // ── Derived data ──
    const attSummary = attendanceData?.summary || {};
    const attTrends = attendanceData?.trends || [];
    const leaveSummary = leaveData?.summary || {};
    const leaveByType = leaveData?.byType || [];
    const leaveTrends = leaveData?.trends || [];
    const deptList = Array.isArray(deptAttendance) ? deptAttendance : [];

    const deptChartData = deptList.map(d => ({
        name: d.department || 'Unknown',
        present: d.present || 0,
        absent: d.absent || 0,
        rate: d.attendanceRate || 0,
    }));

    const deptRateData = [...deptChartData]
        .sort((a, b) => b.rate - a.rate)
        .map(d => ({ name: d.name, rate: Number(d.rate) || 0 }));

    const trendChartData = attTrends.map(t => ({
        date: t.date ? format(new Date(t.date), 'dd MMM') : t.month || '',
        present: t.present || 0,
        absent: t.absent || 0,
        attendanceRate: t.attendanceRate || 0,
    }));

    const leaveTypeChartData = leaveByType.map(l => ({
        name: l.type || l.leave_type || 'Unknown',
        value: l.count || l.totalDays || 0,
    }));

    const leaveTrendChartData = leaveTrends.map(t => ({
        name: t.month || '',
        count: t.count || 0,
        days: t.totalDays || 0,
    }));

    // Comparison deltas
    const getChange = (field) => {
        if (!comparisonData) return undefined;
        const att = comparisonData?.attendance || comparisonData;
        return att?.[field + 'Change'] ?? att?.changes?.[field] ?? undefined;
    };

    // Leave balance filtering
    const filteredBalance = useMemo(() => {
        let data = Array.isArray(leaveBalance) ? leaveBalance : [];
        if (balanceSearch) {
            const q = balanceSearch.toLowerCase();
            data = data.filter(e => (e.name || '').toLowerCase().includes(q) || (e.department || '').toLowerCase().includes(q));
        }
        return balanceShowAll ? data : data.slice(0, 50);
    }, [leaveBalance, balanceSearch, balanceShowAll]);

    // ─── Filter handler ──
    const handleFilterChange = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    // ═══════════════════════════════════════════════════════════════════
    // RENDER: Sub-tab navigation
    // ═══════════════════════════════════════════════════════════════════
    const renderSubTabs = () => (
        <div className={`flex border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} mb-6 overflow-x-auto`}>
            {SUB_TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeSubTab === tab.id;
                return (
                    <button
                        key={tab.id}
                        onClick={() => setActiveSubTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                            isActive
                                ? `border-blue-500 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`
                                : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
                        }`}
                    >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════
    // RENDER: Overview Tab
    // ═══════════════════════════════════════════════════════════════════
    const renderOverview = () => (
        <div className="space-y-6">
            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <StatCard title="Present Days" value={attSummary.present || 0} color="green" change={getChange('present')} />
                <StatCard title="Absent Days" value={attSummary.absent || 0} color="red" change={getChange('absent')} />
                <StatCard title="Half Days" value={attSummary.halfDay || 0} color="yellow" change={getChange('halfDay')} />
                <StatCard title="On Leave" value={attSummary.onLeave || 0} color="blue" change={getChange('onLeave')} />
                <StatCard
                    title="Avg Attendance"
                    value={attSummary.avgAttendance ? `${Number(attSummary.avgAttendance).toFixed(1)}%` : '-'}
                    color="purple"
                    change={getChange('avgAttendance')}
                />
            </div>

            {/* Department Attendance */}
            <ChartContainer
                title="Department Attendance"
                description="Click a department bar to see employee details"
            >
                {deptChartData.length > 0 ? (
                    <BarChartComponent
                        data={deptChartData}
                        xKey="name"
                        bars={[
                            { dataKey: 'present', name: 'Present', color: '#10B981' },
                            { dataKey: 'absent', name: 'Absent', color: '#EF4444' },
                        ]}
                        height={350}
                        formatXAxis={false}
                    />
                ) : (
                    <EmptyState message="No department attendance data for the selected period" />
                )}
                {/* Click overlay for drill-down */}
                {deptChartData.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                        {deptChartData.map(d => (
                            <button
                                key={d.name}
                                onClick={() => loadDrilldownData('department', d.name)}
                                className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                                    isDarkMode
                                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                        : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                {d.name}
                            </button>
                        ))}
                    </div>
                )}
            </ChartContainer>

            {/* Two-Column Quick Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Attendance Rate by Department */}
                <ChartContainer title="Attendance Rate by Department" description="Percentage attendance rate">
                    {deptRateData.length > 0 ? (
                        <BarChartComponent
                            data={deptRateData}
                            xKey="name"
                            bars={[{ dataKey: 'rate', name: 'Rate %', color: '#4F46E5' }]}
                            height={300}
                            horizontal={true}
                        />
                    ) : (
                        <EmptyState message="No data available" />
                    )}
                </ChartContainer>

                {/* Leave Overview Mini */}
                <ChartContainer
                    title="Leave Overview"
                    description="Quick summary"
                    actions={
                        <button
                            onClick={() => setActiveSubTab('leaves')}
                            className={`text-xs px-3 py-1 rounded-lg ${isDarkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-50'}`}
                        >
                            View Details
                        </button>
                    }
                >
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className={`text-center p-3 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                            <p className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{leaveSummary.approved || 0}</p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Approved</p>
                        </div>
                        <div className={`text-center p-3 rounded-lg ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                            <p className={`text-2xl font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{leaveSummary.pending || 0}</p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pending</p>
                        </div>
                        <div className={`text-center p-3 rounded-lg ${isDarkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                            <p className={`text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{leaveSummary.rejected || 0}</p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rejected</p>
                        </div>
                    </div>
                    {leaveTypeChartData.length > 0 ? (
                        <PieChartComponent
                            data={leaveTypeChartData}
                            dataKey="value"
                            nameKey="name"
                            height={200}
                            showLabel={true}
                        />
                    ) : (
                        <EmptyState message="No leave data" />
                    )}
                </ChartContainer>
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════
    // RENDER: Trends Tab
    // ═══════════════════════════════════════════════════════════════════
    const renderTrends = () => (
        <div className="space-y-6">
            {/* Area Chart: Daily Attendance */}
            <ChartContainer title="Daily Attendance Trends" description="Present vs absent over the selected period">
                {trendChartData.length > 0 ? (
                    <AreaChartComponent
                        data={trendChartData}
                        xKey="date"
                        areas={[
                            { dataKey: 'present', name: 'Present', color: '#10B981' },
                            { dataKey: 'absent', name: 'Absent', color: '#EF4444' },
                        ]}
                        height={350}
                        stacked={true}
                        formatXAxis={false}
                    />
                ) : (
                    <EmptyState message="No attendance trend data for this period" />
                )}
            </ChartContainer>

            {/* Line Chart: Attendance Rate */}
            <ChartContainer title="Attendance Rate Trend" description="Attendance rate percentage over time">
                {trendChartData.length > 0 ? (
                    <LineChartComponent
                        data={trendChartData}
                        xKey="date"
                        lines={[
                            { dataKey: 'attendanceRate', name: 'Attendance Rate %', color: '#4F46E5' },
                        ]}
                        height={300}
                        formatXAxis={false}
                    />
                ) : (
                    <EmptyState message="No trend data available" />
                )}
            </ChartContainer>

            {/* Department Comparison */}
            {comparisonData && (
                <ChartContainer title="Department Comparison" description="Current vs previous period attendance">
                    <BarChartComponent
                        data={deptChartData}
                        xKey="name"
                        bars={[
                            { dataKey: 'rate', name: 'Current Period %', color: '#4F46E5' },
                        ]}
                        height={300}
                    />
                </ChartContainer>
            )}
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════
    // RENDER: Leaves Tab
    // ═══════════════════════════════════════════════════════════════════
    const renderLeaves = () => (
        <div className="space-y-6">
            {/* Leave Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard title="Approved" value={leaveSummary.approved || 0} color="green" />
                <StatCard title="Pending" value={leaveSummary.pending || 0} color="yellow" />
                <StatCard title="Rejected" value={leaveSummary.rejected || 0} color="red" />
                <StatCard title="Total Leaves" value={leaveSummary.total || 0} color="blue" />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartContainer title="Leave Type Distribution">
                    {leaveTypeChartData.length > 0 ? (
                        <PieChartComponent
                            data={leaveTypeChartData}
                            dataKey="value"
                            nameKey="name"
                            height={300}
                            showLabel={true}
                        />
                    ) : (
                        <EmptyState message="No leave data for this period" />
                    )}
                </ChartContainer>

                <ChartContainer title="Monthly Leave Trends">
                    {leaveTrendChartData.length > 0 ? (
                        <BarChartComponent
                            data={leaveTrendChartData}
                            xKey="name"
                            bars={[
                                { dataKey: 'count', name: 'Requests', color: '#4F46E5' },
                                { dataKey: 'days', name: 'Days Taken', color: '#F59E0B' },
                            ]}
                            height={300}
                        />
                    ) : (
                        <EmptyState message="No leave trends data" />
                    )}
                </ChartContainer>
            </div>

            {/* Leave Balance Table */}
            <ChartContainer title="Leave Balance Overview" description="Employee-wise leave balances">
                {/* Search */}
                <div className="mb-4">
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                        <Search className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <input
                            type="text"
                            placeholder="Search by employee or department..."
                            value={balanceSearch}
                            onChange={(e) => setBalanceSearch(e.target.value)}
                            className={`bg-transparent border-none outline-none text-sm flex-1 ${
                                isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                            }`}
                        />
                        {balanceSearch && (
                            <button onClick={() => setBalanceSearch('')}>
                                <X className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            </button>
                        )}
                    </div>
                </div>

                {filteredBalance.length > 0 ? (
                    <>
                        <div className="overflow-x-auto max-h-96">
                            <table className="w-full text-sm">
                                <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                    <tr>
                                        <th className={`px-4 py-3 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Employee</th>
                                        <th className={`px-4 py-3 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Department</th>
                                        <th className={`px-4 py-3 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Leave Type</th>
                                        <th className={`px-4 py-3 text-right font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Total</th>
                                        <th className={`px-4 py-3 text-right font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Used</th>
                                        <th className={`px-4 py-3 text-right font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Remaining</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBalance.map((emp, idx) =>
                                        (emp.balances || [{ type: emp.leaveType, total: emp.totalLeaves, used: emp.usedLeaves, remaining: emp.remainingLeaves }]).map((bal, bIdx) => (
                                            <tr key={`${idx}-${bIdx}`} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                                                {bIdx === 0 && (
                                                    <>
                                                        <td rowSpan={emp.balances?.length || 1} className={`px-4 py-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {emp.name}
                                                        </td>
                                                        <td rowSpan={emp.balances?.length || 1} className={`px-4 py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                            {emp.department || '-'}
                                                        </td>
                                                    </>
                                                )}
                                                <td className={`px-4 py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{bal.type || bal.leaveType || '-'}</td>
                                                <td className={`px-4 py-2 text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{bal.total ?? bal.totalLeaves ?? '-'}</td>
                                                <td className={`px-4 py-2 text-right ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{bal.used ?? bal.usedLeaves ?? '-'}</td>
                                                <td className={`px-4 py-2 text-right font-medium ${
                                                    (bal.remaining ?? bal.remainingLeaves ?? 0) <= 0 ? 'text-red-500' : isDarkMode ? 'text-green-400' : 'text-green-600'
                                                }`}>
                                                    {bal.remaining ?? bal.remainingLeaves ?? '-'}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {!balanceShowAll && Array.isArray(leaveBalance) && leaveBalance.length > 50 && (
                            <button
                                onClick={() => setBalanceShowAll(true)}
                                className={`mt-3 w-full py-2 text-sm font-medium rounded-lg border transition-colors ${
                                    isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                Show All ({leaveBalance.length} employees)
                            </button>
                        )}
                    </>
                ) : (
                    <EmptyState message={balanceSearch ? 'No matching employees found' : 'No leave balance data available'} />
                )}
            </ChartContainer>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════
    // RENDER: Heatmap Tab
    // ═══════════════════════════════════════════════════════════════════
    const renderHeatmap = () => {
        const [hmYear, hmMonth] = heatmapMonth.split('-').map(Number);
        const daysInMonth = getDaysInMonth(new Date(hmYear, hmMonth - 1));
        const dayHeaders = Array.from({ length: daysInMonth }, (_, i) => i + 1);
        const employees = heatmapData?.employees || [];

        // Compute per-day totals
        const dayTotals = dayHeaders.map(day => {
            const dateStr = `${hmYear}-${String(hmMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            let present = 0, absent = 0;
            employees.forEach(emp => {
                const status = emp.attendance?.[dateStr];
                if (status === 'present') present++;
                else if (status === 'absent') absent++;
            });
            return { day, present, absent };
        });

        return (
            <div className="space-y-6">
                {/* Month Selector */}
                <div className="flex items-center gap-3">
                    <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <select
                        value={heatmapMonth}
                        onChange={(e) => setHeatmapMonth(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm ${
                            isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    >
                        {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>

                {loading.heatmap ? (
                    <LoadingState message="Loading heatmap data..." />
                ) : error.heatmap ? (
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
                        <p>Failed to load heatmap: {error.heatmap}</p>
                        <button onClick={loadHeatmapData} className="mt-2 text-sm underline">Retry</button>
                    </div>
                ) : employees.length === 0 ? (
                    <EmptyState message={`No attendance records for ${monthOptions.find(o => o.value === heatmapMonth)?.label || heatmapMonth}`} />
                ) : (
                    <ChartContainer title="Attendance Heatmap" description={`${employees.length} employees`}>
                        {/* Legend */}
                        <div className="flex flex-wrap gap-3 mb-4">
                            {Object.entries(STATUS_COLORS).filter(([key]) =>
                                ['present', 'absent', 'half_day', 'leave', 'weekend', 'holiday', 'wfh'].includes(key)
                            ).map(([key, val]) => (
                                <div key={key} className="flex items-center gap-1.5">
                                    <div className={`w-4 h-4 rounded-sm ${isDarkMode ? val.dark : val.light}`} />
                                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{val.label}</span>
                                </div>
                            ))}
                            <div className="flex items-center gap-1.5">
                                <div className={`w-4 h-4 rounded-sm ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`} />
                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>No Data</span>
                            </div>
                        </div>

                        {/* Heatmap Grid */}
                        <div className="overflow-x-auto">
                            <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: '2px' }}>
                                <thead>
                                    <tr>
                                        <th className={`sticky left-0 z-10 px-3 py-2 text-left font-medium whitespace-nowrap ${
                                            isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'
                                        }`}>
                                            Employee
                                        </th>
                                        {dayHeaders.map(day => (
                                            <th key={day} className={`px-0 py-2 text-center font-medium w-7 ${
                                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                            }`}>
                                                {day}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {employees.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((emp, idx) => (
                                        <tr key={emp.employeeId || idx}>
                                            <td className={`sticky left-0 z-10 px-3 py-1 font-medium whitespace-nowrap ${
                                                isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
                                            }`}>
                                                {emp.name || 'Unknown'}
                                            </td>
                                            {dayHeaders.map(day => {
                                                const dateStr = `${hmYear}-${String(hmMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                                const status = emp.attendance?.[dateStr] || '';
                                                const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
                                                const colorConfig = STATUS_COLORS[normalizedStatus] || DEFAULT_STATUS_COLOR;
                                                const dayOfWeek = new Date(hmYear, hmMonth - 1, day).getDay();
                                                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                                                const cellColor = !status && isWeekend
                                                    ? (isDarkMode ? STATUS_COLORS.weekend.dark : STATUS_COLORS.weekend.light)
                                                    : (isDarkMode ? colorConfig.dark : colorConfig.light);

                                                return (
                                                    <td key={day} className="px-0 py-1">
                                                        <div
                                                            className={`w-7 h-7 rounded-sm ${cellColor} cursor-default`}
                                                            title={`${emp.name} — ${format(new Date(hmYear, hmMonth - 1, day), 'dd MMM yyyy')} — ${status || (isWeekend ? 'Weekend' : 'No Data')}`}
                                                        />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Daily Summary Row */}
                        <div className="mt-4">
                            <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Daily Summary (Present / Absent)
                            </p>
                            <div className="flex gap-0.5 overflow-x-auto" style={{ marginLeft: '140px' }}>
                                {dayTotals.map(d => (
                                    <div key={d.day} className="flex flex-col items-center w-7" title={`Day ${d.day}: ${d.present} present, ${d.absent} absent`}>
                                        <div className="w-5 bg-green-500 rounded-t-sm" style={{ height: `${Math.max(2, (d.present / Math.max(employees.length, 1)) * 20)}px` }} />
                                        <div className="w-5 bg-red-500 rounded-b-sm" style={{ height: `${Math.max(1, (d.absent / Math.max(employees.length, 1)) * 20)}px` }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </ChartContainer>
                )}
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════
    // RENDER: Drill-Down Modal
    // ═══════════════════════════════════════════════════════════════════
    const renderDrilldownModal = () => {
        if (!drilldown.open) return null;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setDrilldown({ open: false, type: null, label: '', data: [] })}>
                <div className="absolute inset-0 bg-black/50" />
                <div
                    className={`relative w-full max-w-3xl max-h-[80vh] rounded-xl shadow-2xl overflow-hidden ${
                        isDarkMode ? 'bg-gray-800' : 'bg-white'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div>
                            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {drilldown.type === 'department' ? `Department: ${drilldown.label}` : drilldown.label}
                            </h3>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {drilldown.data.length} employees
                            </p>
                        </div>
                        <button
                            onClick={() => setDrilldown({ open: false, type: null, label: '', data: [] })}
                            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="overflow-y-auto max-h-[60vh] p-6">
                        {loading.drilldown ? (
                            <LoadingState message="Loading employee details..." />
                        ) : drilldown.data.length === 0 ? (
                            <EmptyState message="No employee data found" />
                        ) : (
                            <table className="w-full text-sm">
                                <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                    <tr>
                                        <th className={`px-4 py-3 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Employee</th>
                                        <th className={`px-4 py-3 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Status</th>
                                        <th className={`px-4 py-3 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Check In</th>
                                        <th className={`px-4 py-3 text-left font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Check Out</th>
                                        <th className={`px-4 py-3 text-right font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drilldown.data.map((emp, i) => {
                                        const statusColor = {
                                            present: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
                                            absent: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
                                            half_day: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
                                            leave: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
                                        };
                                        const status = (emp.status || '').toLowerCase().replace(/\s+/g, '_');
                                        return (
                                            <tr key={emp.id || i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                                                <td className={`px-4 py-3 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {emp.name}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                        isDarkMode
                                                            ? (statusColor[status]?.split(' ').filter(c => c.startsWith('dark:')).map(c => c.replace('dark:', '')).join(' ') || 'bg-gray-700 text-gray-300')
                                                            : (statusColor[status]?.split(' ').filter(c => !c.startsWith('dark:')).join(' ') || 'bg-gray-100 text-gray-700')
                                                    }`}>
                                                        {emp.status || '-'}
                                                    </span>
                                                </td>
                                                <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                    {emp.checkInTime || emp.checkIn || '-'}
                                                </td>
                                                <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                    {emp.checkOutTime || emp.checkOut || '-'}
                                                </td>
                                                <td className={`px-4 py-3 text-right font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                    {emp.workingHours ? `${Number(emp.workingHours).toFixed(1)}h` : '-'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════
    // MAIN RENDER
    // ═══════════════════════════════════════════════════════════════════
    return (
        <div className="space-y-4">
            {/* Header Row: Filters + Export */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                    <AnalyticsFilters
                        onFilterChange={handleFilterChange}
                        showDateRange={true}
                        showPeriodSelector={true}
                        showComparison={true}
                    />
                </div>
                <div className="flex items-center gap-2 pt-1">
                    <button
                        onClick={loadMainData}
                        disabled={loading.main}
                        className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                            isDarkMode
                                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        <RefreshCw className={`w-4 h-4 ${loading.main ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={loading.export}
                        className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                        {loading.export ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        <span>Export</span>
                    </button>
                </div>
            </div>

            {/* Sub-Tabs */}
            {renderSubTabs()}

            {/* Tab Content */}
            {loading.main ? (
                <LoadingState message="Loading attendance analytics..." />
            ) : error.main ? (
                <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
                    <p>Failed to load analytics: {error.main}</p>
                    <button onClick={loadMainData} className="mt-2 text-sm underline">Retry</button>
                </div>
            ) : (
                <>
                    {activeSubTab === 'overview' && renderOverview()}
                    {activeSubTab === 'trends' && renderTrends()}
                    {activeSubTab === 'leaves' && renderLeaves()}
                    {activeSubTab === 'heatmap' && renderHeatmap()}
                </>
            )}

            {/* Drill-Down Modal */}
            {renderDrilldownModal()}
        </div>
    );
};

export default AttendanceAnalytics;
